export function formatTripType(t) {
  if (!t) return null;
  const types = {
    "ROUND": "Round trip",
    "ONE_WAY": "One way",
    "HOURLY": "Hourly",
    "OUTSTATION": "Outstation"
  };
  return types[t] || t;
}

export function formatDateTime(s) {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  } catch {
    return null;
  }
}

export function formatTripDuration(lead) {
  if (!lead) return null;
  
  if (lead.tripType === "HOURLY") {
    const hrs = Number(lead.durationHours) || 0;
    return hrs > 0 ? `${hrs} hr package` : null;
  }

  if (lead.pickupDateTime && lead.dropDateTime) {
    const start = new Date(lead.pickupDateTime);
    const end = new Date(lead.dropDateTime);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      const ms = end.getTime() - start.getTime();
      if (ms > 0) {
        const totalHrs = Math.floor(ms / (1000 * 60 * 60));
        const days = Math.floor(totalHrs / 24);
        const remHrs = totalHrs % 24;
        
        if (days > 0) {
          return `${days} day${days > 1 ? 's' : ''}${remHrs > 0 ? ` ${remHrs} hr${remHrs > 1 ? 's' : ''}` : ''}`;
        } else if (totalHrs > 0) {
          return `${totalHrs} hr${totalHrs > 1 ? 's' : ''}`;
        }
      }
    }
  }
  
  return null;
}

/* The locale to write dates in for the app's language - month names in the reader's
   own script (28 సెప్టెం, 28 सित॰). */
const DATE_LOCALES = { en: "en-IN", te: "te-IN", hi: "hi-IN", ta: "ta-IN", kn: "kn-IN" };
export function dateLocale(lang) {
  return DATE_LOCALES[lang] || "en-IN";
}

/* The time of day the way people say it - "ఉదయం 8:00", "रात 8:05", "இரவு 8:05",
   "ರಾತ್ರಿ 8:05": the part of the day first, then the clock, with no AM/PM. English
   stays as it was ("8:05 pm"), with the caller's own clock options. */
const DAY_PARTS = {
  te: ["రాత్రి", "ఉదయం", "మధ్యాహ్నం", "సాయంత్రం"],
  hi: ["रात", "सुबह", "दोपहर", "शाम"],
  ta: ["இரவு", "காலை", "மதியம்", "மாலை"],
  kn: ["ರಾತ್ರಿ", "ಬೆಳಿಗ್ಗೆ", "ಮಧ್ಯಾಹ್ನ", "ಸಂಜೆ"],
};
// Night until 4 in the morning, morning until noon, afternoon until 4, evening until 8.
const partOfDay = (h) => (h < 4 || h >= 20 ? 0 : h < 12 ? 1 : h < 16 ? 2 : 3);
const CLOCK = { hour: "numeric", minute: "2-digit" };

const isDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());

export function clockText(d, lang, englishClock = CLOCK) {
  if (!isDate(d)) return "";
  const words = DAY_PARTS[lang];
  if (!words) return d.toLocaleTimeString(dateLocale(lang), englishClock);
  const h = d.getHours();
  return `${words[partOfDay(h)]} ${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/* A date and its time: "1 అక్టో, ఉదయం 8:00" - and in English, as before, "1 Oct, 8:00 am". */
export function dateTimeText(d, lang, dateParts = { day: "numeric", month: "short" }, englishClock = CLOCK) {
  if (!isDate(d)) return "";
  const loc = dateLocale(lang);
  if (!DAY_PARTS[lang]) return d.toLocaleString(loc, { ...dateParts, ...englishClock });
  return `${d.toLocaleDateString(loc, dateParts)}, ${clockText(d, lang)}`;
}

/* A wallet row's words. The server writes its note in English ("Lead fee 35km", "Hire
   lead - ...", "Wallet recharge via UPI"): an English reader sees that note as before,
   and the other languages say what kind of entry it is, in their words. */
const TXN_WORDS = [
  [/^(LEAD_FEE|LEAD_DEDUCTION_PAID|PLATFORM_FEE_DEDUCTION)$/, "lc_fee"],
  [/FAILED$/, "tx_failed"],
  [/RECHARGE$/, "walletTxnRecharge"],
  [/^WELCOME_CREDIT$/, "tx_welcome"],
  [/^REFERRAL_CLAWBACK$/, "tx_referralBack"],
  [/^REFERRAL_CREDIT/, "tx_referral"],
  [/REFUND/, "walletTxnRefund"],
  [/BONUS$/, "tx_bonus"],
];
export function txnText(txn, t, lang) {
  if (!lang || lang === "en") return txn?.description || txn?.type || "";
  const type = String(txn?.type || "").toUpperCase();
  const hit = TXN_WORDS.find(([re]) => re.test(type));
  return t(hit ? hit[1] : "wl_txnOther");
}

/* "{name}" in a translated string, filled in. */
export function fillText(text, vars = {}) {
  return Object.entries(vars).reduce((out, [k, v]) => out.split(`{${k}}`).join(String(v)), String(text || ""));
}
